
import { defineComponent, computed, ref, onMounted } from "vue";
import { useAsyncSpinner } from "@/services/Spinner";
import { MemberApi } from "@/services/Api";
import { Customer } from "@/models/Customer";
import { AxiosResponse } from "axios";
import BaseButton from "@/components/BaseButton.vue";
import { Form } from "vee-validate";
import UpdateModelHelper from "@/helpers/Helpers";

export default defineComponent({
  components: {
    BaseButton
  },
  methods: {
    onChange() {
      if (this.selectedCustomer.orderInformation || this.selectedCustomer.orderInformation === "") {
        if (this.selectedCustomer.externalId && this.isCheckoutPage) {
          this.updateCustomer();
        }
        this.$emit("update:modelValue", this.selectedCustomer.orderInformation);
      }
    },
    validatePhone(value) {
      if (!value || value.length !== 10) {
        return false;
      }

      const regex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/i;
      return regex.test(value);
    },
    validateAddress(value) {
      if (!value) {
        return false;
      }

      const regex = /^[\p{L}0-9\såäöÅÄÖ]*$/u;
      return regex.test(value);
    },
    validateZip(value) {
      const trimmedValue = value.replace(/\s+/g, '');

      if (trimmedValue.length !== 5 || value.length > 6) {
        return false;
      }

      const regex = /^[0-9\s]*$/;
      return regex.test(value);
    }
  },
  props: {
    isCheckoutPage: {
      type: Boolean,
      required: true,
    },
    initialCustomerId: {
      type: String,
      required: false
    }
  },
  emits: { 
      "setCustomer" (customer: Customer) {
        return customer;
      },
      "setCustomerSaved" (value: boolean) {
        return value;
      },
      "update:modelValue" (value: string): boolean {
        return value !== "";
      }
    },
  setup(props, context) {
    const companyCustomers = ref<Customer[]>([]);
    const selectedValue = ref<string>('0');
    const displayResponseMsg = ref<boolean>(false);
    const confirmShipTo = ref<boolean>(false);
    const responseMessage = ref<string | AxiosResponse>();
    const selectedCustomer = ref({
      externalId: '',
      firstName: '',
      lastName: '',
      email: '',
      mobilePhone: '',
      line1: '',
      zip: '',
      city: '',
      orderInformation: ''
    });
    const form = ref<typeof Form | null>(null);

    const isInputValid = computed(() => {
      return (
        selectedCustomer.value.firstName &&
        selectedCustomer.value.lastName &&
        selectedCustomer.value.email &&
        selectedCustomer.value.mobilePhone && selectedCustomer.value.mobilePhone.length === 10 &&
        selectedCustomer.value.line1 &&
        selectedCustomer.value.zip &&
        selectedCustomer.value.city
      );
    });

    const setCustomer = (customerId): void => {
      const customer = companyCustomers.value.find(customer => customer.externalId === customerId);
      responseMessage.value = "";

      if (customer) {
        if(customer.mobilePhone) {
          customer.mobilePhone = customer.mobilePhone.replaceAll(' ', '');
          customer.mobilePhone = customer.mobilePhone.replaceAll('-', '');
          customer.mobilePhone = customer.mobilePhone.replaceAll('+46', '0');
        }
        selectedCustomer.value.externalId = customer.externalId
        selectedCustomer.value.firstName = customer.firstName
        selectedCustomer.value.lastName = customer.lastName
        selectedCustomer.value.email = customer.email
        selectedCustomer.value.mobilePhone = customer.mobilePhone
        selectedCustomer.value.line1 = customer.deliveryAddresses[0].line1
        selectedCustomer.value.zip = customer.deliveryAddresses[0].zip
        selectedCustomer.value.city = customer.deliveryAddresses[0].city
        selectedCustomer.value.orderInformation = customer.orderInformation

        context.emit("setCustomer", customer);
        context.emit("setCustomerSaved", true);
        context.emit("update:modelValue", selectedCustomer.value.orderInformation);

      } else {
        resetCustomer();
        resetFormMessages();
        context.emit("setCustomerSaved", false);
      }
    }

    const onSubmit = (): void => {
      if (selectedValue.value === '0') {
        addNewCustomer()
      }
      else if (selectedCustomer.value.externalId && !props.isCheckoutPage) {
        updateCustomer()
      }
    }

    const addNewCustomer = async() => {
      const registerCompanyCustomerModel = {
        name: selectedCustomer.value.firstName,
        lastName: selectedCustomer.value.lastName,
        email: selectedCustomer.value.email,
        line1: selectedCustomer.value.line1,
        zip: selectedCustomer.value.zip,
        city: selectedCustomer.value.city,
        mobilePhone: selectedCustomer.value.mobilePhone,
        orderInformation: selectedCustomer.value.orderInformation,
        country: "Sweden"
      }

      await useAsyncSpinner(async () => {
        if (isInputValid.value) {
          const model = await MemberApi.registerCompanyCustomer(registerCompanyCustomerModel);
          if (model.success && model.customer) {
            context.emit("setCustomer", model.customer);
            context.emit("setCustomerSaved", true);
            selectedCustomer.value.externalId = model.customer.externalId;
            getCustomersFromCompany();
            if (!props.isCheckoutPage) {
              resetCustomer();
              resetFormMessages();
            } else {
              selectedValue.value = model.customer.externalId;
            }
          }
          responseMessage.value = model.message;
          showResponseMsg();
          scrollToMessage();
        }
      });
    };

    const updateCustomer = async() => {
      const updateCompanyCustomerModel = UpdateModelHelper.createUpdateCompanyCustomerModel(selectedCustomer.value);

      await useAsyncSpinner(async () => {
        if (isInputValid.value) {
          const model = await MemberApi.updateCompanyCustomer(updateCompanyCustomerModel);
          if (model.success) {
            getCustomersFromCompany();
          }
          responseMessage.value = model.message;
          showResponseMsg();
          scrollToMessage();
        }
      });
    };

    const getCustomersFromCompany = async () => {
      await useAsyncSpinner(async () => {
        const model = await MemberApi.getCustomersByCompany();
        companyCustomers.value = model;
      });
    }

    const resetCustomer = () => {
      selectedCustomer.value.externalId = ''
      selectedCustomer.value.firstName = ''
      selectedCustomer.value.lastName = ''
      selectedCustomer.value.email = ''
      selectedCustomer.value.mobilePhone = ''
      selectedCustomer.value.line1 = ''
      selectedCustomer.value.zip = ''
      selectedCustomer.value.city = ''
      selectedCustomer.value.orderInformation = ''
      selectedValue.value = '0';
      displayResponseMsg.value = false;
    }

    const scrollToMessage = async () => {
      setTimeout(() => {
        const element = document.getElementById("name");
        if (element) {
          element.scrollIntoView({behavior: 'smooth'});
        }
      }, 0);
    }

    const showResponseMsg = () => {
      displayResponseMsg.value = true;
      setTimeout(() => {
        displayResponseMsg.value = false;
      }, 7000);
    }

    const disableFields = computed(() => {
      return selectedCustomer.value.externalId !== null && selectedCustomer.value.externalId !== '' && props.isCheckoutPage;
    });

    const resetFormMessages = () => {
      if(form.value) {
        form.value.resetForm();
      }
    }

    onMounted(async () => {
      await getCustomersFromCompany();
      if (props.initialCustomerId !== "") {
        selectedValue.value = props.initialCustomerId ?? '0';
        setCustomer(props.initialCustomerId)
      }
    });

    return {
      companyCustomers,
      selectedCustomer,
      selectedValue,
      confirmShipTo,
      isInputValid,
      responseMessage,
      setCustomer,
      onSubmit,
      updateCustomer,
      displayResponseMsg,
      disableFields,
      form
    };
  },
});
